import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseService } from '../../services/expense';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { isDemoMode } from '../../services/mockData';
import { canonicalizeEmail, findByIdentityWithManager, sameIdentity } from '../../services/identity';
import { Expense, ExpenseStatus, ApprovalActionInput, ApprovalStatus } from '../../types/expense';
import { UserRole } from '../../types/user';
import { generateClient } from 'aws-amplify/api';
import { notifyExpenseManagerApproved, notifyExpenseManagerRejected } from '../../services/inAppNotification';
import { emailExpenseManagerApproved, emailExpenseManagerRejected } from '../../services/emailNotification';
import { getFinanceAdminEmails } from '../../services/userLoader';
import { useNotifications } from '../../contexts/NotificationContext';
import ExpenseList from '../expenses/ExpenseList';
import ExpenseDetail from '../expenses/ExpenseDetail';
import ExpenseForm from '../expenses/ExpenseForm';
import RightSidePanel from '../performance/RightSidePanel';
import InlineLoader from '../common/InlineLoader';
import { StorageService } from '../../services/storage';
import { GraphQLExpenseService } from '../../services/graphqlExpense';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { notifyExpenseSubmitted } from '../../services/inAppNotification';
import { emailExpenseSubmitted } from '../../services/emailNotification';
import { ExpenseCategory, PaymentMethod, CreateExpenseInput } from '../../types/expense';
import './ManagerDashboard.css';
import '../expenses/MyExpensesPage.css';

// Lazy initialize client to avoid "Amplify not configured" error
let client: ReturnType<typeof generateClient> | null = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};
const graphqlExecutionCounts = new Map<string, number>();

// Query to list users
const listUsersQuery = /* GraphQL */ `
  query ListUsers($limit: Int) {
    listUsers(limit: $limit) {
      items {
        id
        email
        name
        department
      }
    }
  }
`;

interface TeamSummary {
  totalTeamExpenses: number;
  pendingApprovals: number;
  approvedThisMonth: number;
  rejectedThisMonth: number;
  teamMemberCount: number;
}

interface QuickApprovalExpense extends Expense {
  submitterName?: string;
  submitterEmail?: string;
  submitterDepartment?: string;
}

interface UserInfo {
  id: string;
  email: string;
  name: string;
  department?: string;
}

/**
 * Manager Dashboard Component
 * Requirements: 6.2 - Manager dashboard with team expense overview
 */
const ManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const [teamExpenses, setTeamExpenses] = useState<QuickApprovalExpense[]>([]);
  const [filteredTeamExpenses, setFilteredTeamExpenses] = useState<QuickApprovalExpense[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<QuickApprovalExpense[]>([]);
  const [summary, setSummary] = useState<TeamSummary>({
    totalTeamExpenses: 0,
    pendingApprovals: 0,
    approvedThisMonth: 0,
    rejectedThisMonth: 0,
    teamMemberCount: 0
  });
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAllPending, setShowAllPending] = useState(false);
  const [activeModal, setActiveModal] = useState<'approved' | 'rejected' | 'team' | 'my-expenses' | null>(null);
  const [myExpenses, setMyExpenses] = useState<Expense[]>([]);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [newExpenseReceiptFiles, setNewExpenseReceiptFiles] = useState<File[]>([]);
  const [newExpenseSubmitting, setNewExpenseSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Filter states for team expenses
  const [showTeamFilters, setShowTeamFilters] = useState(false);
  const [teamFilters, setTeamFilters] = useState({
    employee: '', // email of selected employee
    status: '' as ExpenseStatus | '',
    datePreset: 'all' as 'all' | 'last-week' | 'last-month' | 'custom',
    dateFrom: '',
    dateTo: ''
  });

  // Prevent duplicate API calls (React StrictMode double-mount in dev)
  const loadingRef = useRef(false);

  useEffect(() => {
    if (user && !loadingRef.current) {
      loadingRef.current = true;
      loadManagerData().finally(() => { loadingRef.current = false; });
    }
  }, [user]);

  // Apply filters when teamExpenses or filters change
  useEffect(() => {
    applyTeamFilters();
  }, [teamExpenses, teamFilters]);

  // Apply filters to team expenses
  const applyTeamFilters = () => {
    let filtered = [...teamExpenses];
    
    // Filter by employee
    if (teamFilters.employee) {
      // sameIdentity: an expense filed under the employee's other domain address is still
      // theirs, and an exact match hid those rows from the filter.
      filtered = filtered.filter(expense =>
        sameIdentity(expense.submitterEmail, teamFilters.employee)
      );
    }
    
    // Filter by status
    if (teamFilters.status) {
      filtered = filtered.filter(expense => expense.status === teamFilters.status);
    }
    
    // Filter by date
    if (teamFilters.dateFrom) {
      filtered = filtered.filter(expense => expense.date >= teamFilters.dateFrom);
    }
    if (teamFilters.dateTo) {
      filtered = filtered.filter(expense => expense.date <= teamFilters.dateTo);
    }
    
    setFilteredTeamExpenses(filtered);
  };

  // Handle date preset changes for team filters
  const handleTeamDatePresetChange = (preset: 'all' | 'last-week' | 'last-month' | 'custom') => {
    const now = new Date();
    let dateFrom = '';
    let dateTo = '';
    
    if (preset === 'last-week') {
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      dateFrom = lastWeek.toISOString().split('T')[0];
      dateTo = now.toISOString().split('T')[0];
    } else if (preset === 'last-month') {
      const lastMonth = new Date(now);
      lastMonth.setMonth(now.getMonth() - 1);
      dateFrom = lastMonth.toISOString().split('T')[0];
      dateTo = now.toISOString().split('T')[0];
    }
    
    setTeamFilters(prev => ({
      ...prev,
      datePreset: preset,
      dateFrom: preset === 'custom' ? prev.dateFrom : dateFrom,
      dateTo: preset === 'custom' ? prev.dateTo : dateTo
    }));
  };

  // Clear team filters
  const clearTeamFilters = () => {
    setTeamFilters({
      employee: '',
      status: '',
      datePreset: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Known test users mapping (for when listUsers query doesn't work)
  const knownUsers: Map<string, UserInfo> = new Map([
    ['employee@test.com', { id: 'employee@test.com', email: 'employee@test.com', name: 'John Employee', department: 'Engineering' }],
    ['manager@test.com', { id: 'manager@test.com', email: 'manager@test.com', name: 'Test Manager', department: 'Engineering' }],
    ['admin@test.com', { id: 'admin@test.com', email: 'admin@test.com', name: 'Finance Admin', department: 'Finance' }],
  ]);

  // Helper to extract name from department field (format: "Department | Name")
  const extractNameFromDepartment = (department: string | undefined): string | null => {
    if (!department) return null;
    if (department.includes(' | ')) {
      const parts = department.split(' | ');
      return parts[1] || null;
    }
    return null;
  };

  // Helper to get display name from expense
  const getSubmitterDisplayName = (expense: Expense, userMap: Map<string, UserInfo>): string => {
    // First, try to extract name from department field (new format: "Department | Name")
    const nameFromDept = extractNameFromDepartment((expense as any).department);
    if (nameFromDept) return nameFromDept;
    
    const submitterId = expense.submitterId;
    
    // Check if it's in the user map
    const user = userMap.get(submitterId);
    if (user?.name) return user.name;
    
    // Check known users
    const knownUser = knownUsers.get(submitterId);
    if (knownUser?.name) return knownUser.name;
    
    // If submitterId looks like an email, extract the name part
    if (submitterId.includes('@')) {
      const namePart = submitterId.split('@')[0];
      // Capitalize and format: "john.doe" -> "John Doe"
      return namePart
        .split(/[._-]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    // If it's a UUID-like ID, return "Employee"
    if (submitterId.length > 20) {
      return 'Employee';
    }
    
    return submitterId;
  };

  const loadManagerData = async () => {
    const t0 = performance.now();
    const traceId = `mgr-dashboard-${Math.round(t0)}`;
    console.log('[PROFILE][ManagerDashboard] ═══════════════════════════════════════');
    console.log('[PROFILE][ManagerDashboard] Route load START', { traceId, role: user?.role, email: user?.email });
    try {
      setLoading(true);
      setError(null);
      
      // Load team expenses (ALL expenses from direct reports, or ALL expenses for Super Admin)
      // Super Admin sees all company expenses in Team Approvals (matching Finance dashboard)
      const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

      const t1 = performance.now();
      const teamPromise = isSuperAdmin
        ? ExpenseServiceWrapper.getAllExpenses()
        : ExpenseServiceWrapper.getTeamExpenses(user?.id, user?.email);

      const t2 = performance.now();
      const myExpensesPromise = ExpenseServiceWrapper.getMyExpenses(user?.id, user?.email);
      const t3 = performance.now();
      const usersPromise = (async (): Promise<Map<string, UserInfo>> => {
        const usersStart = performance.now();
        try {
          const source = 'ManagerDashboard';
          const operationName = 'ListUsers';
          const countKey = `${source}:${operationName}`;
          const executionCount = (graphqlExecutionCounts.get(countKey) || 0) + 1;
          graphqlExecutionCounts.set(countKey, executionCount);
          console.log('GRAPHQL:', operationName, { source, executionCount });
          const usersResult = await getClient().graphql({
          query: listUsersQuery,
          variables: { limit: 100 },
          authMode: 'userPool'
          } as any);
          const userMap: Map<string, UserInfo> = new Map(knownUsers);
          const usersData = (usersResult as any).data?.listUsers?.items || [];
          usersData.forEach((u: UserInfo) => {
            userMap.set(u.id, u);
            // Also map by email for fallback
            if (u.email) userMap.set(u.email, u);
          });
          console.log('[ManagerDashboard][Perf] Users query', { traceId, durationMs: Math.round(performance.now() - usersStart), userCount: usersData.length });
          return userMap;
        } catch {
          console.log('[ManagerDashboard][Perf] Users query FAILED (using fallback)', { traceId, durationMs: Math.round(performance.now() - usersStart) });
          return new Map(knownUsers);
        }
      })();

      const [teamResult, myResult, userMap] = await Promise.all([
        teamPromise,
        myExpensesPromise,
        usersPromise
      ]);
      const tParallel = performance.now();
      console.log('[ManagerDashboard][Perf] Parallel queries complete', {
        traceId,
        totalParallelDurationMs: Math.round(tParallel - t1),
        teamExpenseCount: (teamResult.items || []).length,
        myExpenseCount: (myResult.items || []).length,
        isSuperAdmin,
        graphqlCallCount: 3, // team + my + users
      });

      const teamExpenses = teamResult.items || [];
      const managerExpenses = myResult.items || [];
      
      console.log('[Manager Dashboard] My expenses loaded:', managerExpenses.length);
      console.log('[Manager Dashboard] User ID:', user?.id, 'Email:', user?.email);
      
      // Map team expenses with submitter names
      const tEnrich = performance.now();
      const expensesWithNames: QuickApprovalExpense[] = teamExpenses.map((expense: Expense) => {
        // Get display name - first try department field, then user map, then known users
        const displayName = getSubmitterDisplayName(expense, userMap);
        const submitter = userMap.get(expense.submitterId) || knownUsers.get(expense.submitterId);
        
        // Extract department (without the name part if it's in "Dept | Name" format)
        let department = (expense as any).department || 'Unknown';
        if (department.includes(' | ')) {
          department = department.split(' | ')[0];
        }
        
        return {
          ...expense,
          submitterName: displayName,
          submitterEmail: submitter?.email || expense.submitterId,
          submitterDepartment: department
        };
      });
      console.log('[ManagerDashboard][Perf] Enrichment (name mapping)', {
        traceId,
        durationMs: Math.round(performance.now() - tEnrich),
        enrichedCount: expensesWithNames.length,
        uniqueSubmitters: new Set(expensesWithNames.map(e => e.submitterEmail)).size,
      });
      
      setTeamExpenses(expensesWithNames);
      setMyExpenses(managerExpenses);
      
      // Filter pending approvals (only SUBMITTED for manager approval)
      const tFilter = performance.now();
      const pending = expensesWithNames.filter((expense: QuickApprovalExpense) => 
        expense.status === ExpenseStatus.SUBMITTED
      );
      setPendingApprovals(pending);
      console.log('[ManagerDashboard][Perf] Pending filter', {
        traceId,
        durationMs: Math.round(performance.now() - tFilter),
        pendingCount: pending.length,
        totalTeamExpenses: expensesWithNames.length,
      });
      
      const tSummary = performance.now();
      calculateSummary(expensesWithNames);
      console.log('[ManagerDashboard][Perf] Summary calculation', {
        traceId,
        durationMs: Math.round(performance.now() - tSummary),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load manager data');
    } finally {
      setLoading(false);
      const totalMs = Math.round(performance.now() - t0);
      console.log('[PROFILE][ManagerDashboard] ═══════════════════════════════════════');
      console.log('[PROFILE][ManagerDashboard] Route COMPLETE', {
        traceId,
        totalDurationMs: totalMs,
      });
      console.log('[PROFILE][ManagerDashboard] ═══════════════════════════════════════');
    }
  };

  const calculateSummary = (expenses: Expense[]) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentUserEmail = user?.email?.toLowerCase() || '';
    const currentUserName = user?.name?.toLowerCase() || '';
    
    // Helper to normalize name
    const normalizeName = (name: string): string => {
      return name.toLowerCase().replace(/[^a-z]/g, '');
    };
    
    // Helper to check if name is generic
    const isGenericName = (name: string): boolean => {
      const lower = name.toLowerCase().trim();
      return lower === 'employee' || lower === 'unknown' || lower === '';
    };
    
    const summary: TeamSummary = {
      totalTeamExpenses: 0,
      pendingApprovals: 0,
      approvedThisMonth: 0,
      rejectedThisMonth: 0,
      teamMemberCount: 0
    };

    // Calculate total team expenses
    summary.totalTeamExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    // Count pending approvals
    summary.pendingApprovals = expenses.filter(expense => 
      expense.status === ExpenseStatus.SUBMITTED
    ).length;
    
    // Count approvals/rejections this month
    expenses.forEach(expense => {
      const expenseDate = new Date(expense.updatedAt);
      if (expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear) {
        if (expense.status === ExpenseStatus.MANAGER_APPROVED || 
            expense.status === ExpenseStatus.FINANCE_APPROVED ||
            expense.status === ExpenseStatus.REIMBURSED) {
          summary.approvedThisMonth++;
        } else if (expense.status === ExpenseStatus.MANAGER_REJECTED) {
          summary.rejectedThisMonth++;
        }
      }
    });
    
    // Count unique team members - only count those with real emails
    const uniqueEmails = new Set<string>();
    expenses.forEach(expense => {
      const expenseAny = expense as any;
      const submitterName = expenseAny.submitterName || '';
      const submitterEmail = expenseAny.submitterEmail || '';
      
      // Skip current manager
      /*
       * sameIdentity, so the manager is skipped whichever of their two addresses the expense
       * was filed under. With an exact match, an expense submitted on their other domain was
       * NOT recognised as theirs — so the manager counted as one of their own team members.
       */
      if (sameIdentity(submitterEmail, currentUserEmail)) return;
      if (currentUserName && normalizeName(submitterName) === normalizeName(currentUserName)) return;
      
      // Skip finance admin
      const lowerEmail = submitterEmail.toLowerCase();
      const lowerName = submitterName.toLowerCase();
      if (lowerEmail.includes('admin') || lowerEmail.includes('finance')) return;
      if (lowerName.includes('admin') || lowerName.includes('finance')) return;
      
      // Only count if has real email
      if (submitterEmail && submitterEmail.includes('@')) {
        uniqueEmails.add(submitterEmail.toLowerCase());
      }
    });
    summary.teamMemberCount = uniqueEmails.size;

    setSummary(summary);
  };

  const handleQuickApproval = async (expense: Expense, action: 'approve' | 'reject', comments?: string) => {
    try {
      setActionLoading(expense.id);
      
      if (action === 'approve' && user) {
        await ExpenseServiceWrapper.approveExpense(expense.id, user.id, comments);
        
        // Fetch finance admin emails for notifications
        let financeAdminEmails: string[] = [];
        try {
          financeAdminEmails = await getFinanceAdminEmails();
          console.log('[ManagerDashboard] Finance admin emails for notification:', financeAdminEmails);
        } catch (err) {
          console.warn('[ManagerDashboard] Could not fetch finance admin emails:', err);
        }
        
        // Get submitter email for employee notification
        const submitterEmail = (expense as any).submitterEmail || expense.submitterId;
        
        // Notify employee + finance admins (in-app)
        notifyExpenseManagerApproved(
          submitterEmail,
          financeAdminEmails,
          user.name,
          expense.title,
          expense.amount,
          expense.id
        );
        
        // Email notifications
        emailExpenseManagerApproved(
          submitterEmail,
          financeAdminEmails,
          user.name,
          expense.title,
          expense.amount,
          expense.id
        );
      } else if (user && comments) {
        await ExpenseServiceWrapper.rejectExpense(expense.id, user.id, comments);
        
        // Get submitter email for employee notification
        const submitterEmail = (expense as any).submitterEmail || expense.submitterId;
        
        // Notify employee
        notifyExpenseManagerRejected(
          submitterEmail, user.name, expense.title, expense.id, comments
        );
        // Email notification
        emailExpenseManagerRejected(
          submitterEmail, user.name, expense.title, expense.id, comments
        );
      }
      
      // Reload data to reflect changes
      await loadManagerData();
      refresh();
    } catch (error) {
      console.error(`Failed to ${action} expense:`, error);
      alert(`Failed to ${action} expense. Please try again.`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveWithComments = (expense: Expense) => {
    const comments = prompt('Enter approval comments (optional):');
    if (comments !== null) { // User didn't cancel
      handleQuickApproval(expense, 'approve', comments);
    }
  };

  const handleRejectWithComments = (expense: Expense) => {
    const comments = prompt('Enter rejection reason:');
    if (comments !== null && comments.trim()) { // User provided comments
      handleQuickApproval(expense, 'reject', comments);
    } else if (comments !== null) {
      alert('Rejection reason is required.');
    }
  };

  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingApprovals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingApprovals.map(e => e.id)));
    }
  };

  // Bulk approve
  const handleBulkApprove = async (ids: string[]) => {
    if (ids.length === 0) return;
    const label = ids.length === pendingApprovals.length ? 'ALL' : ids.length;
    if (!window.confirm(`Approve ${label} expense(s)?`)) return;
    setBulkLoading(true);
    let success = 0;
    for (const id of ids) {
      const expense = pendingApprovals.find(e => e.id === id);
      if (!expense) continue;
      try {
        await handleQuickApproval(expense, 'approve', 'Bulk approved');
        success++;
      } catch { /* continue */ }
    }
    setBulkLoading(false);
    setSelectedIds(new Set());
    if (success > 0) {
      await loadManagerData();
      refresh();
    }
  };

  // Handle expense creation for manager
  const handleCreateExpense = async (expenseData: any, receipts?: File[]) => {
    try {
      if (!user) throw new Error('User not found');
      
      // Pass user role and manager email to determine workflow routing
      // submitterId is an email on every row in this table, and the employee create
      // path already writes one — this path was passing user.id, a Cognito sub, so
      // manager-created expenses were keyed differently from everything else.
      // Canonicalised so the row lands on the current domain even when the session
      // belongs to the old-domain account. Falls back to the sub only if no email.
      const newExpense = await ExpenseServiceWrapper.createExpense(
        expenseData,
        canonicalizeEmail(user.email) || user.id,
        user.name,
        user.role,
        user.managerEmail
      );
      
      // Upload receipts if provided
      if (receipts && receipts.length > 0) {
        // Handle receipt uploads here if needed
      }
      
      setShowExpenseForm(false);
      await loadManagerData(); // Reload to show new expense
      alert('Expense created successfully!');
    } catch (error) {
      console.error('Failed to create expense:', error);
      alert(`Failed to create expense: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleSaveDraft = async (expenseData: any, receipts?: File[]) => {
    try {
      if (!user) throw new Error('User not found');
      
      // Save as draft (status will be DRAFT)
      const draftExpense = await ExpenseServiceWrapper.saveDraft(expenseData, user.id);
      
      setShowExpenseForm(false);
      await loadManagerData();
      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Failed to save draft:', error);
      alert(`Failed to save draft: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const formatAmount = (amount: number): string => {
    return ExpenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: ExpenseStatus): string => {
    return ExpenseService.getStatusColor(status);
  };

  // Get approved expenses this month
  const getApprovedThisMonth = (): QuickApprovalExpense[] => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return teamExpenses.filter(expense => {
      const expenseDate = new Date(expense.updatedAt);
      return expenseDate.getMonth() === currentMonth && 
             expenseDate.getFullYear() === currentYear &&
             (expense.status === ExpenseStatus.MANAGER_APPROVED || 
              expense.status === ExpenseStatus.FINANCE_APPROVED ||
              expense.status === ExpenseStatus.REIMBURSED);
    });
  };

  // Get rejected expenses this month
  const getRejectedThisMonth = (): QuickApprovalExpense[] => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return teamExpenses.filter(expense => {
      const expenseDate = new Date(expense.updatedAt);
      return expenseDate.getMonth() === currentMonth && 
             expenseDate.getFullYear() === currentYear &&
             expense.status === ExpenseStatus.MANAGER_REJECTED;
    });
  };

  // Get unique team members (direct reportees only, excluding manager and finance admin)
  // Deduplicates by email or by checking if names are related
  const getTeamMembers = (): { id: string; name: string; email: string; department: string; expenseCount: number }[] => {
    const memberMap = new Map<string, { id: string; name: string; email: string; department: string; expenseCount: number }>();
    
    // Get current user's email and name to exclude them
    const currentUserEmail = user?.email?.toLowerCase() || '';
    const currentUserName = user?.name?.toLowerCase() || '';
    
    // Helper to normalize name for comparison
    const normalizeName = (name: string): string => {
      return name.toLowerCase().replace(/[^a-z]/g, '');
    };
    
    // Helper to check if name is generic/placeholder
    const isGenericName = (name: string): boolean => {
      const lower = name.toLowerCase().trim();
      return lower === 'employee' || lower === 'unknown' || lower === '';
    };
    
    // Helper to check if two names likely refer to same person
    // "Employee" should match with "John Employee" or any name containing "employee"
    const namesMatch = (name1: string, name2: string): boolean => {
      const n1 = normalizeName(name1);
      const n2 = normalizeName(name2);
      if (n1 === n2) return true;
      // If one is just "employee", it matches any name with "employee" in it
      if (n1 === 'employee' && n2.includes('employee')) return true;
      if (n2 === 'employee' && n1.includes('employee')) return true;
      return false;
    };
    
    // First pass: collect all entries, preferring those with real emails
    const allEntries: { submitterName: string; submitterEmail: string; submitterId: string; department: string }[] = [];
    
    teamExpenses.forEach(expense => {
      const submitterName = expense.submitterName || '';
      const submitterEmail = expense.submitterEmail || '';
      const submitterId = expense.submitterId || '';
      
      // Skip if this is the current manager
      /*
       * sameIdentity, so the manager is skipped whichever of their two addresses the expense
       * was filed under. With an exact match, an expense submitted on their other domain was
       * NOT recognised as theirs — so the manager counted as one of their own team members.
       */
      if (sameIdentity(submitterEmail, currentUserEmail)) return;
      if (currentUserName && normalizeName(submitterName) === normalizeName(currentUserName)) return;
      
      // Skip finance admin
      const lowerEmail = submitterEmail.toLowerCase();
      const lowerName = submitterName.toLowerCase();
      if (lowerEmail.includes('admin') || lowerEmail.includes('finance')) return;
      if (lowerName.includes('admin') || lowerName.includes('finance')) return;
      
      allEntries.push({
        submitterName,
        submitterEmail,
        submitterId,
        department: expense.submitterDepartment || 'Unknown'
      });
    });
    
    // Second pass: deduplicate and merge
    allEntries.forEach(entry => {
      const { submitterName, submitterEmail, submitterId, department } = entry;
      
      // Try to find existing entry by email first
      let existingKey: string | null = null;
      
      // Check by email if it's a real email
      if (submitterEmail && submitterEmail.includes('@')) {
        if (memberMap.has(submitterEmail.toLowerCase())) {
          existingKey = submitterEmail.toLowerCase();
        }
      }
      
      // If not found by email, check by name match
      if (!existingKey) {
        for (const [key, member] of memberMap.entries()) {
          if (namesMatch(member.name, submitterName)) {
            existingKey = key;
            break;
          }
        }
      }
      
      if (existingKey) {
        // Merge with existing
        const existing = memberMap.get(existingKey)!;
        existing.expenseCount++;
        
        // Update with better info
        if (!isGenericName(submitterName) && isGenericName(existing.name)) {
          existing.name = submitterName;
        }
        if (submitterEmail && submitterEmail.includes('@') && !existing.email.includes('@')) {
          existing.email = submitterEmail;
        }
        if (department !== 'Unknown' && existing.department === 'Unknown') {
          existing.department = department;
        }
      } else {
        // Create new entry - use email as key if available, otherwise use ID
        const key = (submitterEmail && submitterEmail.includes('@')) 
          ? submitterEmail.toLowerCase() 
          : submitterId;
        
        memberMap.set(key, {
          id: submitterId,
          name: submitterName || 'Unknown',
          email: submitterEmail || submitterId,
          department: department,
          expenseCount: 1
        });
      }
    });
    
    // Filter out entries that only have generic names and UUID emails (likely duplicates we couldn't merge)
    const result = Array.from(memberMap.values()).filter(member => {
      // Keep if has real email
      if (member.email.includes('@')) return true;
      // Keep if has real name
      if (!isGenericName(member.name)) return true;
      // Skip generic entries with UUID-like IDs
      return false;
    });
    
    return result;
  };

  if (loading) {
    return (
      <div className="manager-dashboard">
        <div className="loading"><InlineLoader message="Loading manager dashboard" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-dashboard">
        <div className="error">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={loadManagerData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-dashboard">
      <div className="dashboard-header">
        <h1>Manager Dashboard</h1>
        <p>Team expense management for {user?.name}</p>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Team Expenses</h3>
          <div className="amount">{formatAmount(summary.totalTeamExpenses)}</div>
          <div className="description">All team expenses</div>
        </div>
        
        <div className="summary-card pending">
          <h3>Pending Approvals</h3>
          <div className="count">{summary.pendingApprovals}</div>
          <div className="description">Awaiting your approval</div>
        </div>
        
        <div 
          className="summary-card approved clickable"
          onClick={() => setActiveModal('approved')}
          role="button"
          tabIndex={0}
        >
          <h3>Approved This Month</h3>
          <div className="count">{summary.approvedThisMonth}</div>
          <div className="description">Click to view details</div>
        </div>
        
        <div 
          className="summary-card rejected clickable"
          onClick={() => setActiveModal('rejected')}
          role="button"
          tabIndex={0}
        >
          <h3>Rejected This Month</h3>
          <div className="count">{summary.rejectedThisMonth}</div>
          <div className="description">Click to view details</div>
        </div>
        
        <div 
          className="summary-card team clickable"
          onClick={() => setActiveModal('team')}
          role="button"
          tabIndex={0}
        >
          <h3>Team Members</h3>
          <div className="count">{summary.teamMemberCount}</div>
          <div className="description">Click to view details</div>
        </div>
      </div>

      {/* Quick Approval Queue */}
      {pendingApprovals.length > 0 && (
        <div className="quick-approval-section">
          <div className="section-header-with-toggle">
            <h2>Quick Approval Queue ({pendingApprovals.length})</h2>
            <div className="bulk-actions">
              {selectedIds.size > 0 && (
                <button className="tms-btn tms-btn-primary" onClick={() => handleBulkApprove(Array.from(selectedIds))} disabled={bulkLoading}>
                  {bulkLoading ? 'Approving...' : `Approve Selected (${selectedIds.size})`}
                </button>
              )}
              <button className="tms-btn tms-btn-primary" onClick={() => handleBulkApprove(pendingApprovals.map(e => e.id))} disabled={bulkLoading}>
                {bulkLoading ? 'Approving...' : 'Approve All'}
              </button>
              {pendingApprovals.length > 5 && (
                <button 
                  className="toggle-view-button"
                  onClick={() => setShowAllPending(!showAllPending)}
                >
                  {showAllPending ? 'Show Less' : `Show All (${pendingApprovals.length})`}
                </button>
              )}
            </div>
          </div>
          <div className="select-all-row">
            <label className="bulk-checkbox-label">
              <input type="checkbox" checked={selectedIds.size === pendingApprovals.length && pendingApprovals.length > 0} onChange={toggleSelectAll} />
              <span>Select All</span>
            </label>
          </div>
          <div className={`quick-approval-list ${showAllPending ? 'expanded' : ''}`}>
            {(showAllPending ? pendingApprovals : pendingApprovals.slice(0, 5)).map(expense => (
              <div key={expense.id} className={`quick-approval-item ${selectedIds.has(expense.id) ? 'selected' : ''}`}>
                <div className="expense-select-col">
                  <input type="checkbox" checked={selectedIds.has(expense.id)} onChange={() => toggleSelect(expense.id)} />
                </div>
                <div className="expense-info">
                  <div className="expense-header">
                    <h4>{expense.title}</h4>
                    <div className={`expense-status ${getStatusColor(expense.status)}`}>
                      {expense.status}
                    </div>
                  </div>
                  
                  <div className="expense-details">
                    <span className="amount">{formatAmount(expense.amount)}</span>
                    <span className="category">{expense.category}</span>
                    <span className="date">{formatDate(expense.date)}</span>
                    <span className="submitter">👤 {expense.submitterName || 'Unknown'}</span>
                    {expense.submitterDepartment && (
                      <span className="department">📁 {expense.submitterDepartment}</span>
                    )}
                  </div>
                  
                  {expense.description && (
                    <div className="expense-description">{expense.description}</div>
                  )}
                </div>
                
                <div className="quick-actions">
                  <button
                    className="quick-action-button view"
                    onClick={() => setSelectedExpense(expense)}
                    disabled={actionLoading === expense.id}
                  >
                    View Details
                  </button>
                  
                  <button
                    className="quick-action-button approve"
                    onClick={() => handleApproveWithComments(expense)}
                    disabled={actionLoading === expense.id}
                  >
                    {actionLoading === expense.id ? 'Processing...' : 'Approve'}
                  </button>
                  
                  <button
                    className="quick-action-button reject"
                    onClick={() => handleRejectWithComments(expense)}
                    disabled={actionLoading === expense.id}
                  >
                    {actionLoading === expense.id ? 'Processing...' : 'Reject'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Expense Overview */}
      <div className="team-expenses-section">
        <div className="section-header-with-toggle">
          <h2>Team Expense Overview ({filteredTeamExpenses.length})</h2>
          <button 
            className="toggle-filters-button"
            onClick={() => setShowTeamFilters(!showTeamFilters)}
          >
            {showTeamFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
        
        {/* Team Expense Filters */}
        {showTeamFilters && (
          <div className="team-filters">
            {/* Quick Date Filters */}
            <div className="filter-row quick-filters">
              <label>Date:</label>
              <div className="quick-filter-buttons">
                <button 
                  className={`quick-filter-btn ${teamFilters.datePreset === 'all' ? 'active' : ''}`}
                  onClick={() => handleTeamDatePresetChange('all')}
                >
                  All Time
                </button>
                <button 
                  className={`quick-filter-btn ${teamFilters.datePreset === 'last-week' ? 'active' : ''}`}
                  onClick={() => handleTeamDatePresetChange('last-week')}
                >
                  Last Week
                </button>
                <button 
                  className={`quick-filter-btn ${teamFilters.datePreset === 'last-month' ? 'active' : ''}`}
                  onClick={() => handleTeamDatePresetChange('last-month')}
                >
                  Last Month
                </button>
                <button 
                  className={`quick-filter-btn ${teamFilters.datePreset === 'custom' ? 'active' : ''}`}
                  onClick={() => handleTeamDatePresetChange('custom')}
                >
                  Custom
                </button>
              </div>
            </div>
            
            <div className="filter-row">
              {/* Employee Filter */}
              <div className="filter-group">
                <label>Employee:</label>
                <select 
                  value={teamFilters.employee}
                  onChange={(e) => setTeamFilters(prev => ({ ...prev, employee: e.target.value }))}
                >
                  <option value="">All Employees</option>
                  {getTeamMembers().map(member => (
                    <option key={member.email} value={member.email}>
                      {member.name} ({member.expenseCount} expenses)
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Status Filter */}
              <div className="filter-group">
                <label>Status:</label>
                <select 
                  value={teamFilters.status}
                  onChange={(e) => setTeamFilters(prev => ({ ...prev, status: e.target.value as ExpenseStatus | '' }))}
                >
                  <option value="">All Statuses</option>
                  {Object.values(ExpenseStatus).map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              {/* Custom Date Range */}
              {teamFilters.datePreset === 'custom' && (
                <>
                  <div className="filter-group">
                    <label>From:</label>
                    <input 
                      type="date"
                      value={teamFilters.dateFrom}
                      onChange={(e) => setTeamFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                    />
                  </div>
                  <div className="filter-group">
                    <label>To:</label>
                    <input 
                      type="date"
                      value={teamFilters.dateTo}
                      onChange={(e) => setTeamFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              <button className="clear-filters-btn" onClick={clearTeamFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        )}
        
        <ExpenseList 
          expenses={filteredTeamExpenses}
          onExpenseSelect={setSelectedExpense}
          showActions={false}
        />
      </div>

      {/* My Expenses Section */}
      <div className="my-expenses-section">
        <div className="section-header-with-actions">
          <h2>My Expenses ({myExpenses.length})</h2>
          <div className="section-actions">
            <button 
              className="create-expense-button"
              onClick={() => setShowExpenseForm(true)}
            >
              + Create New Expense
            </button>
            <button 
              className="view-all-button"
              onClick={() => setActiveModal('my-expenses')}
            >
              View All
            </button>
          </div>
        </div>
        
        {myExpenses.length > 0 ? (
          <div className="my-expenses-preview">
            {myExpenses.slice(0, 3).map(expense => (
              <div key={expense.id} className="my-expense-item">
                <div className="expense-info">
                  <div className="expense-header">
                    <h4>{expense.title}</h4>
                    <div className={`expense-status ${getStatusColor(expense.status)}`}>
                      {expense.status}
                    </div>
                  </div>
                  <div className="expense-details">
                    <span className="amount">{formatAmount(expense.amount)}</span>
                    <span className="category">{expense.category}</span>
                    <span className="date">{formatDate(expense.date)}</span>
                  </div>
                  {expense.description && (
                    <div className="expense-description">{expense.description}</div>
                  )}
                </div>
                <div className="expense-actions">
                  <button
                    className="view-button"
                    onClick={() => setSelectedExpense(expense)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
            {myExpenses.length > 3 && (
              <div className="more-expenses-indicator">
                And {myExpenses.length - 3} more expenses...
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <p>You haven't created any expenses yet.</p>
            <button 
              className="create-first-expense-button"
              onClick={() => setShowExpenseForm(true)}
            >
              Create Your First Expense
            </button>
          </div>
        )}
      </div>

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div className="expense-detail-modal">
          <div className="modal-backdrop" onClick={() => setSelectedExpense(null)} />
          <div className="modal-content">
            <ExpenseDetail 
              expense={selectedExpense}
              onClose={() => setSelectedExpense(null)}
              onUpdate={loadManagerData}
            />
          </div>
        </div>
      )}

      {/* Approved Expenses Modal */}
      {activeModal === 'approved' && (
        <div className="summary-modal">
          <div className="modal-backdrop" onClick={() => setActiveModal(null)} />
          <div className="summary-modal-content">
            <div className="summary-modal-header">
              <h2>✅ Approved Team Expenses This Month</h2>
              <button className="close-button" onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className="summary-modal-body">
              {getApprovedThisMonth().length > 0 ? (
                <div className="expense-list-modal">
                  {getApprovedThisMonth().map(expense => (
                    <div 
                      key={expense.id} 
                      className="expense-list-item"
                      onClick={() => { setActiveModal(null); setSelectedExpense(expense); }}
                    >
                      <div className="expense-list-info">
                        <h4>{expense.title}</h4>
                        <div className="expense-list-meta">
                          <span className="amount">{formatAmount(expense.amount)}</span>
                          <span className="submitter">👤 {expense.submitterName}</span>
                          <span className="date">{formatDate(expense.date)}</span>
                          <span className={`status ${getStatusColor(expense.status)}`}>{expense.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No approved expenses this month.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rejected Expenses Modal */}
      {activeModal === 'rejected' && (
        <div className="summary-modal">
          <div className="modal-backdrop" onClick={() => setActiveModal(null)} />
          <div className="summary-modal-content">
            <div className="summary-modal-header">
              <h2>❌ Rejected Team Expenses This Month</h2>
              <button className="close-button" onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className="summary-modal-body">
              {getRejectedThisMonth().length > 0 ? (
                <div className="expense-list-modal">
                  {getRejectedThisMonth().map(expense => (
                    <div 
                      key={expense.id} 
                      className="expense-list-item"
                      onClick={() => { setActiveModal(null); setSelectedExpense(expense); }}
                    >
                      <div className="expense-list-info">
                        <h4>{expense.title}</h4>
                        <div className="expense-list-meta">
                          <span className="amount">{formatAmount(expense.amount)}</span>
                          <span className="submitter">👤 {expense.submitterName}</span>
                          <span className="date">{formatDate(expense.date)}</span>
                          <span className={`status ${getStatusColor(expense.status)}`}>{expense.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No rejected expenses this month.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Team Members Modal */}
      {activeModal === 'team' && (
        <div className="summary-modal">
          <div className="modal-backdrop" onClick={() => setActiveModal(null)} />
          <div className="summary-modal-content">
            <div className="summary-modal-header">
              <h2>👥 My Direct Reports</h2>
              <button className="close-button" onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className="summary-modal-body">
              {getTeamMembers().length > 0 ? (
                <div className="team-members-list">
                  {getTeamMembers().map(member => (
                    <div key={member.id} className="team-member-item">
                      <div className="member-avatar">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="member-info">
                        <h4>{member.name}</h4>
                        <div className="member-meta">
                          <span className="email">📧 {member.email}</span>
                          <span className="department">📁 {member.department}</span>
                          <span className="expense-count">📝 {member.expenseCount} expense(s)</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No direct reports found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* My Expenses Modal */}
      {activeModal === 'my-expenses' && (
        <div className="summary-modal">
          <div className="modal-backdrop" onClick={() => setActiveModal(null)} />
          <div className="summary-modal-content">
            <div className="summary-modal-header">
              <h2>💼 My Expenses</h2>
              <div className="modal-header-actions">
                <button 
                  className="create-expense-button"
                  onClick={() => {
                    setActiveModal(null);
                    setShowExpenseForm(true);
                  }}
                >
                  + Create New
                </button>
                <button className="close-button" onClick={() => setActiveModal(null)}>×</button>
              </div>
            </div>
            <div className="summary-modal-body">
              {myExpenses.length > 0 ? (
                <div className="expense-list-modal">
                  {myExpenses.map(expense => (
                    <div 
                      key={expense.id} 
                      className="expense-list-item"
                      onClick={() => { setActiveModal(null); setSelectedExpense(expense); }}
                    >
                      <div className="expense-list-info">
                        <h4>{expense.title}</h4>
                        <div className="expense-list-meta">
                          <span className="amount">{formatAmount(expense.amount)}</span>
                          <span className="category">{expense.category}</span>
                          <span className="date">{formatDate(expense.date)}</span>
                          <span className={`status ${getStatusColor(expense.status)}`}>{expense.status}</span>
                        </div>
                        {expense.description && (
                          <div className="expense-description">{expense.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>You haven't created any expenses yet.</p>
                  <button 
                    className="create-first-expense-button"
                    onClick={() => {
                      setActiveModal(null);
                      setShowExpenseForm(true);
                    }}
                  >
                    Create Your First Expense
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Expense Side Panel */}
      <RightSidePanel isOpen={showExpenseForm} title="New Expense" onClose={() => setShowExpenseForm(false)} wide>
        <form className="my-expenses-form" onSubmit={async (e) => {
          e.preventDefault();
          setNewExpenseSubmitting(true);
          try {
            const fd = new FormData(e.currentTarget);
            const input: CreateExpenseInput = {
              title: fd.get('title') as string,
              category: fd.get('category') as ExpenseCategory,
              amount: parseFloat(fd.get('amount') as string),
              date: fd.get('date') as string,
              description: fd.get('description') as string || '',
              paymentMethod: fd.get('paymentMethod') as PaymentMethod,
            };
            if (newExpenseReceiptFiles.length === 0) {
              alert('Please attach at least one receipt before submitting.');
              setNewExpenseSubmitting(false);
              return;
            }
            const draft = await ExpenseServiceWrapper.createDraft(input, user?.email || user?.id, user?.name, user?.department);
            for (const file of newExpenseReceiptFiles) {
              try {
                const receipt = await StorageService.uploadReceipt(draft.id, file);
                await GraphQLExpenseService.createReceipt({ expenseId: draft.id, fileName: receipt.fileName, fileUrl: receipt.fileUrl, fileKey: receipt.fileKey, fileType: receipt.fileType, fileSize: receipt.fileSize });
              } catch (err) { console.error('Receipt upload failed:', file.name, err); }
            }
            let managerEmail = user?.managerEmail;
            if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
              try {
                const response = await CognitoAdminService.listUsers();
                const cognitoUser = findByIdentityWithManager(response.users, user?.email);
                if (cognitoUser?.managerEmail) managerEmail = cognitoUser.managerEmail;
              } catch {}
            }
            const submitted = await ExpenseServiceWrapper.submitForApproval(draft.id, { role: user?.role, managerEmail });
            const employeeName = user?.name || 'Employee';
            if (submitted.status === ExpenseStatus.SUBMITTED && managerEmail) {
              await notifyExpenseSubmitted(managerEmail, employeeName, input.title, input.amount, draft.id);
              await emailExpenseSubmitted(managerEmail, employeeName, input.title, input.amount, draft.id);
            } else if (submitted.status === ExpenseStatus.MANAGER_APPROVED) {
              const financeEmails = await getFinanceAdminEmails();
              for (const email of financeEmails) {
                await notifyExpenseSubmitted(email, employeeName, input.title, input.amount, draft.id);
                await emailExpenseSubmitted(email, employeeName, input.title, input.amount, draft.id);
              }
            }
            setShowExpenseForm(false);
            setNewExpenseReceiptFiles([]);
            await loadManagerData();
          } catch (err) {
            console.error('Failed to submit expense:', err);
            alert('Failed to submit expense. Please try again.');
          } finally {
            setNewExpenseSubmitting(false);
          }
        }}>
          <div className="my-expenses-form__field">
            <label>Title *</label>
            <input name="title" type="text" required placeholder="e.g. Client dinner, Travel to Mumbai" />
          </div>
          <div className="my-expenses-form__row">
            <div className="my-expenses-form__field">
              <label>Amount (₹) *</label>
              <input name="amount" type="number" step="0.01" min="0.01" required placeholder="0.00" />
            </div>
            <div className="my-expenses-form__field">
              <label>Date *</label>
              <input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} />
            </div>
          </div>
          <div className="my-expenses-form__row">
            <div className="my-expenses-form__field">
              <label>Category *</label>
              <select name="category" required defaultValue={ExpenseCategory.OTHER}>
                {Object.values(ExpenseCategory).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="my-expenses-form__field">
              <label>Payment Method *</label>
              <select name="paymentMethod" required defaultValue={PaymentMethod.PERSONAL_CARD}>
                {Object.values(PaymentMethod).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="my-expenses-form__field">
            <label>Description</label>
            <textarea name="description" rows={3} placeholder="Optional details about this expense" />
          </div>
          <div className="my-expenses-form__field">
            <label>Receipts * <span className="my-expenses-form__hint">(PDF, JPG, PNG — max 10MB)</span></label>
            <div className="my-expenses-form__receipts">
              {newExpenseReceiptFiles.map((f, i) => (
                <div key={i} className="my-expenses-form__receipt-item">
                  <span>{f.name}</span>
                  <button type="button" onClick={() => setNewExpenseReceiptFiles(prev => prev.filter((_, idx) => idx !== i))}>✕</button>
                </div>
              ))}
              <label className="my-expenses-form__upload-btn">
                📎 Add Receipt
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => {
                  if (e.target.files) {
                    const files = Array.from(e.target.files);
                    for (const f of files) {
                      const v = StorageService.validateFile(f);
                      if (!v.isValid) { alert(`${f.name}: ${v.error}`); return; }
                    }
                    setNewExpenseReceiptFiles(prev => [...prev, ...files]);
                  }
                }} style={{ display: 'none' }} />
              </label>
            </div>
          </div>
          <div className="my-expenses-form__actions">
            <button type="button" className="my-expenses-form__cancel" onClick={() => setShowExpenseForm(false)}>Cancel</button>
            <button type="submit" className="my-expenses-form__submit" disabled={newExpenseSubmitting}>
              {newExpenseSubmitting ? 'Submitting...' : 'Submit Expense'}
            </button>
          </div>
        </form>
      </RightSidePanel>
    </div>
  );
};

export default ManagerDashboard;
